'use client';
import {
  createContext,
  type PropsWithChildren,
  useContext,
  useState,
} from 'react';
import type { Mesh } from 'three';
import type { Dictionary, UndecoratedFileVersion } from '@witmetrics/utils';
import { downloadPreview } from '@/utils/files';

export type LoadedFile = Mesh | Blob | string;

type LoadedFilesValue = {
  loadedFiles: Dictionary<LoadedFile>;
  addLoadedFile: (id: string | number, file: LoadedFile) => void;
  addLoadedFiles: (files: Dictionary<LoadedFile>) => void;
  onLoadPreview: (fileVersion: UndecoratedFileVersion) => Promise<void>;
};

const LoadedFilesContext = createContext<LoadedFilesValue | undefined>(
  undefined
);

export default function LoadedFilesProvider({ children }: PropsWithChildren) {
  const [loadedFiles, setLoadedFiles] = useState<
    LoadedFilesValue['loadedFiles']
  >({});

  const handleAddLoadedFile = (id: string | number, file: LoadedFile) => {
    setLoadedFiles((f) => ({ ...f, [id]: file }));
  };

  const handleAddLoadedFiles = (files: Dictionary<LoadedFile>) => {
    setLoadedFiles((f) => ({ ...f, ...files }));
  };

  const handleLoadPreview = async (version: UndecoratedFileVersion) => {
    if (
      !version.previewSource || // no preview available
      !!loadedFiles[`preview-${version.id}`] // preview already loaded
    ) {
      return;
    }
    const arrayBuffer = await downloadPreview(version.id);
    handleAddLoadedFile(
      `preview-${version.id}`,
      URL.createObjectURL(new Blob([arrayBuffer]))
    );
  };

  const contextValue = {
    loadedFiles,
    addLoadedFile: handleAddLoadedFile,
    addLoadedFiles: handleAddLoadedFiles,
    onLoadPreview: handleLoadPreview,
  };

  return (
    <LoadedFilesContext.Provider value={contextValue}>
      {children}
    </LoadedFilesContext.Provider>
  );
}

export function useLoadedFiles() {
  const context = useContext(LoadedFilesContext);
  if (!context) {
    throw Error('useLoadedFiles must be used within a LoadedFileProvider');
  }
  return context;
}
